import * as THREE from 'three/webgpu';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    MaterialApplySource,
} from './AbstractInteractionVisualState';
import type {
    InteractionVisualStateContext,
    InteractionVisualStateRuntime,
} from './AbstractInteractionVisualState';
import type { Viewer } from '../../viewer/Viewer';
import { ImportedModelFeatureTreeVisualApplicator } from '../../imported-model/interaction/ImportedModelFeatureTreeVisualApplicator';
import { ImportedModelBodyTreeSelectionFaceHighlighter } from '../../imported-model/interaction/ImportedModelBodyTreeSelectionFaceHighlighter';
import { ImportedModelSelectionObjectResolver } from '../../imported-model/selection/ImportedModelSelectionObjectResolver';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { solidworksDefaultPreset } from '../../imported-model/render/solidworks-default-preset';
import { markSharedInteractionMaterial } from '../../material/materialOwnership';
import {
    ImportedModelInstancedTopologyFaceVisualProjector,
} from '../../imported-model/instancing/selection';

//------ 线性阵列来源对象粉色高亮 开始------
/**
 * 线性阵列特征树来源高亮颜色。
 */
enum LinearComponentPatternTreeSourceHighlightColor {
    /** 源零部件 A 的粉色来源提示。 */
    SourceComponent = 0xff8bd0,
}

/**
 * 线性阵列特征树来源高亮材质缓存键。
 */
enum LinearComponentPatternTreeSourceHighlightMaterialCacheKey {
    /** 源零部件 mesh 高亮材质缓存键。 */
    SourceComponent = 'assembly-linear-component-pattern-source-component-mesh',
}
//------ 线性阵列来源对象粉色高亮 结束------

/**
 * 装配体模式视觉状态。
 *
 * 设计说明：
 * 1. 装配体模式直接继承抽象视觉状态，保持装配体视觉边界独立；
 * 2. 正式基准对象 hover / selected 通过抽象基类受保护方法显式复用；
 * 3. imported-model 设计树交互通过共享应用器输出，避免与 Default 模式逻辑漂移。
 */
export class AssemblyTransitionState extends AbstractInteractionVisualState {
    //------ 装配体导入模型树交互视觉 开始------
    /** imported-model 设计树视觉应用器。 */
    private readonly importedModelFeatureTreeApplicator: ImportedModelFeatureTreeVisualApplicator;
    /** imported-model 设计树 Body 整模型覆盖高亮器。 */
    private readonly importedBodyTreeModelHighlighter: ImportedModelBodyTreeSelectionFaceHighlighter;
    /** imported-model 选择对象解析器。 */
    private readonly importedModelSelectionObjectResolver: ImportedModelSelectionObjectResolver;
    /** 实例化 imported-model 拓扑面的单槽视觉投影器。 */
    private readonly importedModelInstancedTopologyFaceVisualProjector: ImportedModelInstancedTopologyFaceVisualProjector;

    /**
     * 创建装配体模式视觉状态。
     *
     * @param runtime 交互视觉状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
        this.importedModelSelectionObjectResolver = new ImportedModelSelectionObjectResolver({
            scene: this.viewer.scene,
            getSceneObjectById: (objectId: string) => this.viewer.findSceneObjectById(objectId),
        });
        this.importedModelInstancedTopologyFaceVisualProjector = new ImportedModelInstancedTopologyFaceVisualProjector(
            sourceMesh => this.viewer.resolveImportedModelInstancedMeshUnit(sourceMesh),
        );
        this.importedModelFeatureTreeApplicator = new ImportedModelFeatureTreeVisualApplicator({
            viewer: this.viewer,
            runtime: this.runtime,
            snapshotOriginalMaterial: (
                object: THREE.Mesh | THREE.Line | THREE.Sprite,
            ) => this.snapshotOriginalMaterial(object),
            selectedMeshMaterialCacheKey: 'assembly-imported-model-tree-selected-mesh',
        });
        this.importedBodyTreeModelHighlighter = new ImportedModelBodyTreeSelectionFaceHighlighter({
            unitSource: this.viewer.getImportedModelInstancedUnitSource?.(),
            idleRenderScheduler: this.viewer,
            membershipSource: this.viewer.meshManager?.getSceneObjectMembershipSource?.(),
            getSourceRevision: () => this.viewer.getImportedModelRenderSourceRevision?.() ?? 0,
            listSourceHierarchyChildren: this.viewer.listImportedModelSourceHierarchyChildren
                ? object => this.viewer.listImportedModelSourceHierarchyChildren(object) : undefined,
            isImportedModelObject: (object) => this.importedModelSelectionObjectResolver.isImportedModelObject(object),
            resolveInstancedMeshUnit: (sourceMesh) => (
                this.viewer.resolveImportedModelInstancedMeshUnit(sourceMesh)
            ),
            findSceneObjectById: (objectId) => this.viewer.findSceneObjectById(objectId),
            listInstancedSourceMeshes: (rootObject) => (
                this.viewer.listImportedModelInstancedSourceMeshes?.(rootObject) ?? []
            ),
            getSelectedBodyMaterial: () => (
                this.importedModelFeatureTreeApplicator.getImportedModelTreeSelectedBodyMaterial()
            ),
        });
    }

    /**
     * 刷新装配体模式视觉状态。
     *
     * 设计说明：
     * 1. 先复用抽象基类基础点线面视觉状态，再追加 imported-model 装配语义；
     * 2. Mate 命令态已接管的拓扑代理必须从普通状态机剥离，避免 overlay 与普通 selected 材质双重表达；
     * 3. 其他场景里直接命中的 `FeatureFace / FeatureLine / FeaturePoint` 仍沿用基础状态机，不走树交互分支。
     *
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        const baseVisualStateContext = this.createBaseVisualStateContext(context);
        super.refreshVisualState(baseVisualStateContext);
        this.importedModelInstancedTopologyFaceVisualProjector.apply(baseVisualStateContext);
        //------ 线性阵列来源对象粉色高亮 开始------
        this.applyLinearComponentPatternSourceHighlight(context);
        //------ 线性阵列来源对象粉色高亮 结束------
        this.applyDatumReferenceInteractionStates(baseVisualStateContext);
        this.importedBodyTreeModelHighlighter.apply(context);
        this.importedModelFeatureTreeApplicator.applyFeatureTreeVisualState(context);
    }

    /**
     * 重置装配体模式额外资源。
     */
    public override resetVisualState(): void {
        this.importedModelInstancedTopologyFaceVisualProjector.reset();
        super.resetVisualState();
        this.importedBodyTreeModelHighlighter.reset();
        this.importedModelFeatureTreeApplicator.resetVisualState();
    }

    /** 结束状态生命周期时释放热态选择缓存，普通取消选择仍保留稳定批次。 */
    public override dispose(): void {
        super.dispose();
        this.importedBodyTreeModelHighlighter.dispose();
    }

    /**
     * 判断当前对象是否应被装配体模式默认态材质接管。
     *
     * @param object 待判断对象。
     * @param context 当前刷新上下文。
     * @returns 当前对象允许进入默认态材质接管时返回 `true`。
     */
    protected override shouldApplyDefaultMaterialState(
        object: THREE.Object3D,
        context: InteractionVisualStateContext,
    ): boolean {
        if (!super.shouldApplyDefaultMaterialState(object, context)) {
            return false;
        }

        return !this.importedModelFeatureTreeApplicator.isImportedModelTopologyProxyObject(object);
    }

    /**
     * 收集 imported-model 语义对象下可用于 mesh 高亮的 visible mesh。
     *
     * @param rootObject 语义根对象。
     * @param options 收集选项。
     * @returns visible mesh 列表。
     */
    protected collectImportedVisibleMeshes(
        rootObject: THREE.Object3D,
        options: {
            explicitObjectIds?: readonly string[];
        } = {},
    ): THREE.Mesh[] {
        return this.importedModelFeatureTreeApplicator.collectImportedVisibleMeshes(rootObject, options);
    }

    /**
     * 获取 imported-model 特征树选中 mesh 高亮材质。
     *
     * @returns 共享高亮材质。
     */
    protected getImportedModelTreeSelectedMeshMaterial(): THREE.MeshStandardMaterial {
        return this.importedModelFeatureTreeApplicator.getImportedModelTreeSelectedMeshMaterial();
    }

    /**
     * 获取 imported-model 实例整模型覆盖使用的 Selected 材质。
     *
     * @returns 适用于单槽实例覆盖对象的共享高亮材质。
     */
    protected getImportedModelTreeSelectedBodyMaterial(): THREE.MeshPhongMaterial {
        return this.importedModelFeatureTreeApplicator.getImportedModelTreeSelectedBodyMaterial();
    }

    //------ 线性阵列来源对象粉色高亮 开始------
    /**
     * 获取线性阵列源零部件高亮材质。
     *
     * 设计说明：
     * 1. 源对象 A 不是当前真实 selectedObjects，不能复用默认 selected 蓝色；
     * 2. 固定使用用户指定的 `#ff8bd0`，表达“该对象是阵列来源”而不是“该对象被主选”；
     * 3. 材质写入共享缓存并标记所有权，避免交互状态恢复时误释放仍被缓存引用的材质。
     *
     * @returns 源零部件高亮材质。
     */
    private getLinearComponentPatternSourceMeshMaterial(): THREE.MeshStandardMaterial {
        const cacheKey = LinearComponentPatternTreeSourceHighlightMaterialCacheKey.SourceComponent;
        const cachedMaterial = this.runtime.stateMeshMaterialCache.get(cacheKey);
        if (cachedMaterial instanceof THREE.MeshStandardMaterial) {
            return cachedMaterial;
        }

        const nextMaterial = markSharedInteractionMaterial(new THREE.MeshStandardMaterial({
            color: LinearComponentPatternTreeSourceHighlightColor.SourceComponent,
            transparent: true,
            opacity: 0.42,
            metalness: solidworksDefaultPreset.material.baseMetalness,
            roughness: solidworksDefaultPreset.material.baseRoughness,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide,
        }));
        this.runtime.stateMeshMaterialCache.set(cacheKey, nextMaterial);
        return nextMaterial;
    }

    /**
     * 应用线性阵列源零部件粉色高亮。
     *
     * 设计说明：
     * 1. 仅响应特征树线性阵列分组选中写入的 `linearComponentPatternSourceObjectIds`；
     * 2. 复制体 A1/A2 仍由 selectedObjects 原链路处理，源对象 A 只在这里做视觉提示；
     * 3. 每次刷新前父类都会恢复旧材质，因此这里只需要快照并替换当前帧命中的 visible mesh。
     *
     * @param context 当前刷新上下文。
     */
    private applyLinearComponentPatternSourceHighlight(context: InteractionVisualStateContext): void {
        if (
            context.selectedInteractionMeta?.source !== 'feature-tree'
            || context.selectedInteractionMeta.semantic !== 'entity'
        ) {
            return;
        }

        const sourceObjectIds = this.resolveLinearComponentPatternSourceObjectIds(context);
        if (sourceObjectIds.length === 0) {
            return;
        }

        const selectedObjectIds = new Set(context.selectedObjects.map((object) => object.uuid));
        const sourceMaterial = this.getLinearComponentPatternSourceMeshMaterial();

        sourceObjectIds.forEach((sourceObjectId) => {
            if (selectedObjectIds.has(sourceObjectId)) {
                return;
            }

            const sourceObject = this.viewer.findSceneObjectById(sourceObjectId);
            if (!sourceObject) {
                return;
            }

            this.collectImportedVisibleMeshes(sourceObject).forEach((mesh) => {
                this.snapshotOriginalMaterial(mesh);
                mesh.material = sourceMaterial;
                /**
                 * 写入状态机是为了在同一轮刷新内阻止 hover 等后续状态覆盖来源提示；
                 * 它不代表源对象进入真实 selectedObjects。
                 */
                this.runtime.objectStateMachine.set(mesh.uuid, GeometryInteractionState.Selected);
            });
        });
    }

    /**
     * 从交互元信息解析线性阵列源对象 ID。
     *
     * @param context 当前刷新上下文。
     * @returns 去重后的源对象 ID 列表。
     */
    private resolveLinearComponentPatternSourceObjectIds(
        context: InteractionVisualStateContext,
    ): string[] {
        const rawSourceObjectIds = context.selectedInteractionMeta?.linearComponentPatternSourceObjectIds;
        if (!Array.isArray(rawSourceObjectIds)) {
            return [];
        }

        const sourceObjectIdSet = new Set<string>();
        rawSourceObjectIds.forEach((sourceObjectId) => {
            const normalizedSourceObjectId = sourceObjectId.trim();
            if (normalizedSourceObjectId.length > 0) {
                sourceObjectIdSet.add(normalizedSourceObjectId);
            }
        });

        return [...sourceObjectIdSet];
    }
    //------ 线性阵列来源对象粉色高亮 结束------

    /**
     * 判断对象是否为 imported-model 子树里的运行时拓扑代理。
     *
     * @param object 待判断对象。
     * @returns 命中 imported-model Face / Line / Point 代理时返回 `true`。
     */
    protected isImportedModelTopologyProxyObject(object: THREE.Object3D): boolean {
        return this.importedModelFeatureTreeApplicator.isImportedModelTopologyProxyObject(object);
    }

    /**
     * 生成交给默认状态机的基础上下文。
     *
     * @param context 原始刷新上下文。
     * @returns 供默认状态机使用的基础上下文。
     */
    private createBaseVisualStateContext(context: InteractionVisualStateContext): InteractionVisualStateContext {
        const commandTopologyProxyIds = new Set(
            (context.commandSelectedTopologyProxyObjects ?? [])
                .filter((object) => this.isTopologyProxyInteractionObject(object))
                .map((object) => object.uuid),
        );
        const filteredContext = this.importedModelFeatureTreeApplicator.createFilteredBaseContext(context);
        const filteredHoveredObject = this.filterCommandTopologyProxyObject(
            filteredContext.hoveredObject,
            commandTopologyProxyIds,
        );
        const selectedObjectsWithoutCommandProxies = filteredContext.selectedObjects
            .filter((selectedObject) => !commandTopologyProxyIds.has(selectedObject.uuid));

        return {
            ...filteredContext,
            hoveredObject: filteredHoveredObject,
            selectedObjects: selectedObjectsWithoutCommandProxies,
        };
    }

    /**
     * 过滤已由命令态 overlay 接管的拓扑代理对象。
     *
     * @param object 当前普通 hover 对象。
     * @param commandTopologyProxyIds 命令态接管的拓扑代理 ID 集。
     * @returns 可交给普通视觉状态的对象；被命令态接管时返回 `null`。
     */
    private filterCommandTopologyProxyObject(
        object: THREE.Object3D | null,
        commandTopologyProxyIds: ReadonlySet<string>,
    ): THREE.Object3D | null {
        if (!object || !commandTopologyProxyIds.has(object.uuid)) {
            return object;
        }
        return this.isTopologyProxyInteractionObject(object) ? null : object;
    }

    /**
     * 判断对象是否为普通视觉状态需要避让的拓扑代理。
     *
     * @param object 待判断对象。
     * @returns 是 FeatureFace / FeatureLine / FeaturePoint 时返回 true。
     */
    private isTopologyProxyInteractionObject(object: THREE.Object3D): boolean {
        return isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object);
    }
    //------ 装配体导入模型树交互视觉 结束------
}
